import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AuthGuardOptions {
  redirectTo?: string;
  showToast?: boolean;
}

interface SupabaseError {
  code?: string;
  message?: string;
  status?: number;
}

/**
 * Hook that provides authentication guard functionality.
 * Verifies session validity before critical operations and handles expired sessions.
 */
export function useAuthGuard(options: AuthGuardOptions = {}) {
  const { redirectTo = '/auth', showToast = true } = options;
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();

  /**
   * Checks if the current session is valid
   */
  const isAuthenticated = useCallback(() => {
    return !!user && !!session;
  }, [user, session]);

  /**
   * Verifies session with the server before critical operations
   */
  const verifySession = useCallback(async (): Promise<boolean> => {
    if (!session) {
      if (showToast) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
      navigate(redirectTo);
      return false;
    }

    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error || !data.user) {
        // Session is invalid - clear state and redirect
        await signOut();
        if (showToast) {
          toast.error('Sessão expirada. Faça login novamente.');
        }
        navigate(redirectTo);
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Session verification failed:', error);
      await signOut();
      if (showToast) {
        toast.error('Erro de autenticação. Faça login novamente.');
      }
      navigate(redirectTo);
      return false;
    }
  }, [session, signOut, navigate, redirectTo, showToast]);

  /**
   * Handles Supabase errors, detecting auth failures
   */
  const handleAuthError = useCallback(async (error: SupabaseError): Promise<boolean> => {
    const isAuthError = 
      error?.code === 'PGRST301' || // JWT expired
      error?.code === '401' ||
      error?.code === '403' ||
      error?.message?.toLowerCase().includes('jwt') ||
      error?.message?.toLowerCase().includes('session') ||
      error?.message?.toLowerCase().includes('unauthorized') ||
      error?.status === 401 ||
      error?.status === 403;

    if (isAuthError) {
      await signOut();
      if (showToast) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
      navigate(redirectTo);
      return true;
    }

    return false;
  }, [signOut, navigate, redirectTo, showToast]);

  /**
   * Wraps a Supabase operation with auth error handling
   */
  const withAuthGuard = useCallback(
    async <T>(operation: () => Promise<{ data: T | null; error: SupabaseError | null }>): Promise<{ data: T | null; error: SupabaseError | null; authFailed: boolean }> => {
      // Quick check before operation
      if (!isAuthenticated()) {
        if (showToast) {
          toast.error('Você precisa estar logado para realizar esta ação.');
        }
        navigate(redirectTo);
        return { data: null, error: null, authFailed: true };
      }

      try {
        const result = await operation();

        if (result.error) {
          const wasAuthError = await handleAuthError(result.error);
          if (wasAuthError) {
            return { data: null, error: result.error, authFailed: true };
          }
        }

        return { ...result, authFailed: false };
      } catch (error) {
        console.error('Operation failed:', error);
        return { data: null, error: error as SupabaseError, authFailed: false };
      }
    },
    [isAuthenticated, handleAuthError, navigate, redirectTo, showToast]
  );

  /**
   * Requires authentication before executing a callback
   */
  const requireAuth = useCallback(
    async (callback: () => Promise<void> | void): Promise<boolean> => {
      const isValid = await verifySession();
      if (isValid) {
        await callback();
        return true;
      }
      return false;
    },
    [verifySession]
  );

  return {
    isAuthenticated,
    verifySession,
    handleAuthError,
    withAuthGuard,
    requireAuth,
    user,
    session,
  };
}
